// ============================================================
// Platform Control · Security — auth hardening, trust
// enforcement, alerts, and key posture. Navigates to real
// OLIVIUM screens and wires the Rotate Keys action.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ViewShell, { Card, Row, Section } from './_ViewShell'
import { SHELL_TOKENS, text } from '../../../shell/styles'
import { api, isAuthError } from '../../../api/client'

export default function SecurityView() {
  const navigate = useNavigate()
  const [rotating, setRotating] = useState(false)
  const [rotateResult, setRotateResult] = useState<string | null>(null)

  // Live posture from /api/admin/security-posture
  const [posture, setPosture] = useState<{
    active_sessions: number; active_lockouts: number
    open_alerts: number; denials_today: number
  } | null>(null)

  const fetchPosture = useCallback(async () => {
    try {
      const r = await api.admin.securityPosture()
      setPosture(r.posture)
    } catch (err) {
      if (!isAuthError(err)) console.warn('[SecurityView] posture:', err)
    }
  }, [])

  useEffect(() => { void fetchPosture() }, [fetchPosture])

  async function handleRotate() {
    if (rotating) return
    const reason = window.prompt('Rotation reason (required):')
    if (!reason) return
    setRotating(true)
    setRotateResult(null)
    try {
      const result = await api.admin.rotateKeys(reason)
      setRotateResult(`Rotated. New prefix: ${result.newPublicKeyPrefix}`)
    } catch (err) {
      setRotateResult(`Failed: ${(err as Error).message}`)
    } finally {
      setRotating(false)
    }
  }

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · SECURITY"
      title="Security"
      description="Auth hardening, trust enforcement, alerts, and key posture."
    >
      <Section label="LIVE POSTURE">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
          <StatusTile label="Sessions" value={posture ? String(posture.active_sessions) : 'hardened'} tone={SHELL_TOKENS.green} />
          <StatusTile label="Open Alerts" value={posture ? String(posture.open_alerts) : '...'} tone={(posture?.open_alerts ?? 0) > 0 ? SHELL_TOKENS.amber : SHELL_TOKENS.green} />
          <StatusTile label="Lockouts" value={posture ? String(posture.active_lockouts) : '0'} tone={(posture?.active_lockouts ?? 0) > 0 ? SHELL_TOKENS.red : SHELL_TOKENS.green} />
          <StatusTile label="Denials Today" value={posture ? String(posture.denials_today) : '0'} tone={(posture?.denials_today ?? 0) > 0 ? SHELL_TOKENS.amber : SHELL_TOKENS.green} />
        </div>
      </Section>

      <Section label="AUTH HARDENING">
        <Card>
          <Row label="Login path" value="POST /api/auth/login" hint="server-side, not direct Supabase" />
          <Row label="Rate limiting" value="20/15min by IP, 5/15min by email" hint="Redis-backed, fails open without Redis" />
          <Row label="Account lockout" value="threshold-based" hint="via olv_account_lockouts" />
          <Row label="Audit" value="every attempt" hint="success + failure in olv_audit_trail" />
          <Row label="Session creation" value="olv_sessions" hint="with device trust fingerprinting" />
        </Card>
      </Section>

      <Section label="TRUST CONTROLS">
        <Card>
          <Row label="Audit trail" value="hash-chained" hint="immutable, DB trigger computes chain_hash" />
          <Row label="Chain verification" value="real" hint="olv.audit.verifyChainIntegrity()" />
          <Row label="Document seals" value="real" hint="content hash in olv_document_seals" />
          <Row label="RLS coverage" value="100%" hint="all olv_* and sq_* tables" />
          <Row label="Admin gating" value="minLevel: 80" hint="role-based via sq_memberships + olv_roles" />
        </Card>
      </Section>

      <Section label="ALERT DELIVERY">
        <Card>
          <Row label="Channel" value="Slack (webhook)" hint="via fireAlert() → securityAlert()" />
          <Row label="Triggers" value="severity: critical or alert" hint="from olv.audit.log()" />
          <Row label="Safety" value="delivery never breaks audit chain" hint="audit persisted before delivery" />
          <Row label="Fallback" value="console.warn" hint="always fires regardless of delivery" />
        </Card>
      </Section>

      <Section label="OPERATOR ACTIONS">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" onClick={() => navigate('/square/olivium/sessions')} style={navBtnStyle}>
            View Sessions
          </button>
          <button type="button" onClick={() => navigate('/square/olivium/alerts')} style={navBtnStyle}>
            View Alerts
          </button>
          <button type="button" onClick={() => navigate('/square/olivium/feed')} style={navBtnStyle}>
            Open Audit Trail
          </button>
          <button
            type="button"
            onClick={handleRotate}
            disabled={rotating}
            style={{
              ...navBtnStyle,
              borderColor: SHELL_TOKENS.brandLine,
              background: SHELL_TOKENS.brandSoft,
              color: SHELL_TOKENS.brand,
              opacity: rotating ? 0.6 : 1,
              cursor: rotating ? 'wait' : 'pointer',
            }}
          >
            {rotating ? 'Rotating…' : 'Rotate Keys'}
          </button>
        </div>
        {rotateResult && (
          <div style={{
            marginTop: 10,
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: rotateResult.startsWith('Failed') ? SHELL_TOKENS.red : SHELL_TOKENS.green,
          }}>
            {rotateResult}
          </div>
        )}
      </Section>
    </ViewShell>
  )
}

function StatusTile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{
      padding: '12px 14px',
      borderRadius: 10,
      background: SHELL_TOKENS.surface,
      border: `1px solid ${SHELL_TOKENS.border}`,
    }}>
      <div style={{
        ...text.kicker,
        fontSize: 8,
        letterSpacing: '0.18em',
        color: SHELL_TOKENS.inkFaint,
        marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 14,
        fontWeight: 700,
        color: tone || SHELL_TOKENS.ink,
        fontFamily: 'var(--font-mono)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}>
        {value}
      </div>
    </div>
  )
}

const navBtnStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 10,
  border: `1px solid ${SHELL_TOKENS.border}`,
  background: 'transparent',
  color: SHELL_TOKENS.ink,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontFamily: 'var(--font-mono)',
  cursor: 'pointer',
}
